import React from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons/faChartLine';

import AnonEmployerAssociation from 'buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import AuthorDisplayLoading from 'buyside/components/presentation/IdeaShowPage/AuthorDisplay/authorDisplayLoading';
import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import FollowButton, {
  FollowableType
} from 'buyside/components/presentation/FollowButton/followButton';
import Link from 'buyside/components/presentation/Link/link';
import MessageButton from 'buyside/components/presentation/MessageModal/messageButton';
import ProfileImage, {
  IMAGE_SIZES
} from 'buyside/components/presentation/ProfileImage/profileImage';
import RequestUpdateModalContainer from 'buyside/components/container/IdeaShow/RequestUpdateModalContainer';
import TooltipWhite from 'buyside/components/presentation/Tooltips/TooltipWhite/tooltipWhite';

import {
  employerProfileUrl,
  defaultUserImageUrl,
  rankingUrl,
  userIdeaUrl,
  userUrl
} from 'buyside/util/urlHelper';

import MessageBubbleIcon from 'buyside/svgs/message_bubble.svg';
import RefreshIcon from 'buyside/svgs/refresh.svg';

import {
  Maybe,
  useIdeaCurrentUserQuery,
  useIdeaAuthorQuery,
  useIdeaCoreQuery
} from 'interfaces/graphql-typings';

import styles from './authorDisplayStyles.scss';

const AuthorDisplay: React.FC<{
  displayName?: Maybe<string>;
  ideaId: string;
  isAuthor: boolean;
  isPublic: boolean;
  loading: boolean;
}> = ({ isPublic, displayName, ideaId, isAuthor, loading }) => {
  const { data: currentUserData } = useIdeaCurrentUserQuery({
    variables: { ideaId },
    skip: isPublic
  });
  const { data: ideaAuthorData } = useIdeaAuthorQuery({ variables: { ideaId } });
  const { data: ideaCoreData } = useIdeaCoreQuery({ variables: { ideaId } });

  if (loading) return <AuthorDisplayLoading />;

  const { associatedEmployer, user: ideaAuthor, isNonBuyside } = ideaAuthorData?.idea || {};
  const { anonymous } = ideaCoreData?.idea || {};
  const { isAllocator, canManageMessages, canViewUserDetails, isRetail } =
    currentUserData?.currentUser || {};
  const {
    id: ideaAuthorId,
    mostRecentEmployer,
    activeFollowersCount,
    profileImageUrl,
    profileCity,
    profile: ideaAuthorProfile,
    username
  } = ideaAuthor || {};
  const { ideaAnnualizedReturnMedian, ideaReturnVsBenchmarkAverage, ideaTotalReturnAverage } =
    ideaAuthorProfile || {};
  const { id: employerId, name: employerName, aumRange, fundType, employerProfile: fundProfile } =
    associatedEmployer || mostRecentEmployer?.employer || {};

  const featuredRank = ideaAuthor?.featuredRank;
  const isCurrentEmployer = Boolean(mostRecentEmployer?.currentEmployer);
  const fundFollowerCount = fundProfile?.followerCount || 0;
  const showMessageButtons = Boolean(!anonymous && !isAllocator && !isRetail && canManageMessages);
  const showUserDetails =
    !anonymous && (isAuthor || Boolean(canViewUserDetails) || (!isPublic && Boolean(isNonBuyside)));

  return (
    <div className={styles.authorContainer}>
      <section className={styles.section}>
        <span className={styles.image}>
          <ProfileImage
            source={showUserDetails ? profileImageUrl : defaultUserImageUrl()}
            size={IMAGE_SIZES.SMALLER}
          />
        </span>
        <div className={styles.authorInfo}>
          <div className={styles.heading}>Author:</div>
          <a href={showUserDetails ? userUrl(ideaAuthorId) : '#'} className={styles.nameLink}>
            {displayName}
          </a>
          <div className={styles.detail}>
            {!anonymous && featuredRank && (
              <Link to={rankingUrl(featuredRank.category)}>{featuredRank.rankString}</Link>
            )}
            {!anonymous && featuredRank && !!activeFollowersCount && (
              <span className={styles.pipe}> | </span>
            )}
            {!anonymous && !!activeFollowersCount && (
              <>{`${activeFollowersCount} Follower${activeFollowersCount > 1 ? 's' : ''}`}</>
            )}
            {!anonymous && (
              <span className={styles.tooltipContainer}>
                <TooltipWhite
                  overlay={
                    <div>
                      <h5 className={styles.sidebarHeader}>Research Performance</h5>
                      <h5 className={styles.sidebarSubHeader}>Ideas</h5>
                      <div className={styles.performanceLine}>
                        <span>Total Return (Average):</span>
                        <span className={styles.rightAlign}>{ideaTotalReturnAverage}</span>
                      </div>
                      <div className={styles.performanceLine}>
                        <span>Return vs Benchmark (Average):</span>
                        <span className={styles.rightAlign}>{ideaReturnVsBenchmarkAverage}</span>
                      </div>
                      <div className={styles.performanceLine}>
                        <span>Annualized Return (Median):</span>
                        <span className={styles.rightAlign}>{ideaAnnualizedReturnMedian}</span>
                      </div>
                    </div>
                  }
                  placement="bottom"
                >
                  <div className={styles.chartArea}>
                    <FontAwesomeIcon icon={faChartLine} className={styles.chartIcon} />
                  </div>
                </TooltipWhite>
              </span>
            )}
          </div>
          {!isAuthor && !isPublic && (
            <div>
              <div className={styles.actionItems}>
                {showMessageButtons && ideaAuthorId && displayName && (
                  <>
                    <MessageButton
                      buttonOptions={{
                        text: (
                          <>
                            <MessageBubbleIcon className={styles.bubbleIcon} />
                            Message Author
                          </>
                        ),
                        type: BUTTON_TYPES.LINK
                      }}
                      receiverEmployerName={employerName}
                      isCurrentEmployer={isCurrentEmployer}
                      receiverId={ideaAuthorId}
                      receiverName={displayName}
                    />
                    <RequestUpdateModalContainer
                      buttonOptions={{
                        text: (
                          <>
                            <RefreshIcon className={styles.refreshIcon} />
                            Request Update
                          </>
                        ),
                        type: BUTTON_TYPES.LINK
                      }}
                      ideaId={ideaId}
                      receiverName={displayName}
                    />
                  </>
                )}
                {anonymous ? (
                  username ? (
                    <Button
                      size={BUTTON_SIZES.TINY}
                      text={`More ideas by ${username}`}
                      type={BUTTON_TYPES.LINK}
                      url={userIdeaUrl(encodeURI(username))}
                    />
                  ) : null
                ) : ideaAuthorId ? (
                  <Button
                    size={BUTTON_SIZES.TINY}
                    text={
                      showUserDetails ? `More ideas by ${displayName}` : 'More ideas by this member'
                    }
                    type={BUTTON_TYPES.LINK}
                    url={userIdeaUrl(ideaAuthorId)}
                  />
                ) : null}
              </div>
              {!anonymous && ideaAuthorId && (
                <FollowButton
                  type={FollowableType.USER}
                  id={ideaAuthorId}
                  size={BUTTON_SIZES.TINIEST}
                  analyticsTag="follow-member"
                />
              )}
            </div>
          )}
        </div>
      </section>
      <section className={classNames(styles.section, styles.employer)}>
        <div>
          {employerId && (
            <div>
              <div className={styles.heading}>
                {associatedEmployer
                  ? 'Fund: '
                  : isCurrentEmployer
                  ? 'Currently at: '
                  : 'Formerly at: '}
              </div>
              {showUserDetails ? (
                <a href={employerProfileUrl(employerId)} className={styles.nameLink}>
                  {employerName}
                </a>
              ) : (
                <div>
                  <AnonEmployerAssociation
                    aumRange={aumRange}
                    fundType={fundType}
                    profileCity={profileCity}
                    employerStyles={styles.anonEmployer}
                  />
                </div>
              )}
              {!anonymous && Boolean(fundProfile) && (
                <>
                  {fundFollowerCount > 0 && (
                    <span data-testid="fund-followers-count" className={styles.detail}>
                      {`${fundFollowerCount} Follower${fundFollowerCount > 1 ? 's' : ''}`}
                    </span>
                  )}
                  {!isAuthor && !isPublic && fundProfile?.id && (
                    <div className={styles.actionItems}>
                      <FollowButton
                        type={FollowableType.FUND}
                        id={fundProfile.id}
                        size={BUTTON_SIZES.TINIEST}
                        analyticsTag="follow-fund"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AuthorDisplay;
